import type { IUpdateArticleStockCmd } from "@/data/commands/catalog/UpdateArticleStockCmd";
import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IStock, IStockTableViewArticle } from "@/data/model/Article";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { UiNotification } from "@/portal/ui/Notification";

export class StockTableEditor {
    public $element: JQuery;
    private $editor: JQuery;
    private $activeCheckbox: JQuery;
    private $saveBtn?: JQuery;
    private $disableWarning?: JQuery;
    private initial?: IStock;
    private remainingInput?: NumberInput;
    private articleId: string;
    private isAvailable: boolean;

    public onSaved?: { (m: IStockTableViewArticle) };

    constructor(articleId: string, isAvailable: boolean, stock?: IStock) {
        this.articleId = articleId;
        this.isAvailable = isAvailable;

        this.$element = $('<div class="stockeditor" >');
        this.$editor = $('<div class="editor" />').appendTo(this.$element);

        this.$activeCheckbox = $('<input type="checkbox" class="form-control-check" />').appendTo(this.$editor);
        this.$activeCheckbox.change(() => {
            if (this.$activeCheckbox.is(":checked")) this.setTracked();
            else this.setUntracked();

            this.checkSaveBtn();
        });

        const remainingInput = $('<input type="text" class="form-control" name="remaining" />').appendTo(this.$editor);
        this.remainingInput = new NumberInput(remainingInput, {
            min: 0,
            step: 1,
        });

        this.remainingInput.onChange = () => {
            this.checkSaveBtn();
            this.checkArticleDisableWarning();
        };

        this.setByModel(stock);
    }

    private setByModel(st?: IStock) {
        this.initial = st;
        this.$activeCheckbox.prop("checked", st !== undefined);

        if (st === undefined) this.setUntracked();
        else {
            this.setTracked();
        }
    }

    private setUntracked() {
        this.remainingInput!.setDisabled(true).setValue("");
        if (this.$disableWarning !== undefined) {
            this.$disableWarning.remove();
            this.$disableWarning = undefined;
        }
    }

    private setTracked() {
        this.remainingInput!.setDisabled(false).setValue(this.initial === undefined ? 1 : this.initial.remaining);
    }

    private checkSaveBtn() {
        let newValue: IStock | undefined = undefined;
        if (this.$activeCheckbox.is(":checked")) {
            newValue = {
                remaining: this.remainingInput!.getValue()!,
            };
        }

        const hasChanges =
            (this.initial === undefined && newValue !== undefined) ||
            (this.initial !== undefined && newValue === undefined) ||
            this.initial?.remaining !== newValue?.remaining;

        if (hasChanges && this.$saveBtn === undefined) {
            this.$saveBtn = $('<button class="btn btn-primary">' + i18n("general.save") + "</button>")
                .appendTo(this.$element)
                .click((e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    this.save();
                });
        } else if (!hasChanges && this.$saveBtn !== undefined) {
            this.$saveBtn.remove();
            this.$saveBtn = undefined;
        }
    }

    private checkArticleDisableWarning() {
        let shouldShow = false;
        if (this.$saveBtn !== undefined && this.isAvailable) {
            const checkSafety =
                this.initial === undefined || this.initial.safetyStock === undefined ? 0 : this.initial.safetyStock;
            if (this.remainingInput!.getValue()! <= checkSafety) {
                shouldShow = true;
            }
        }

        if (shouldShow && this.$disableWarning === undefined) {
            this.$disableWarning = $(
                '<div class="disablewarning" title="' +
                    i18n("stock.disableWarning") +
                    '"><i class="flaticon-danger"></i></div>'
            ).insertAfter(this.$editor);
        } else if (!shouldShow && this.$disableWarning !== undefined) {
            this.$disableWarning.remove();
            this.$disableWarning = undefined;
        }
    }

    private save() {
        this.$saveBtn!.prop("disabled", true);

        const isActive = this.$activeCheckbox.is(":checked");
        const data: IUpdateArticleStockCmd = {
            isActive: isActive,
            safetyStock: !isActive ? undefined : this.initial === undefined ? undefined : this.initial.safetyStock,
            notifyThreshold: !isActive
                ? undefined
                : this.initial === undefined
                  ? undefined
                  : this.initial.notifyThreshold,
            remaining: !isActive ? undefined : this.remainingInput!.getValue(),
        };

        Factory.getInstance<CatalogDao>(CatalogDao)
            .setArticleStock(this.articleId, data)
            .then((b) => {
                this.isAvailable = b.isAvailable;
                this.setByModel(b.stock);
                this.$saveBtn!.prop("disabled", false);
                this.checkSaveBtn();
                this.checkArticleDisableWarning();

                if (this.onSaved !== undefined) this.onSaved(b);
            })
            .catch((ex) => {
                logError(ex);
                this.$saveBtn!.prop("disabled", false);
                UiNotification.showException(i18n("stock.failedSave"));
            });
    }

    public destroy() {
        this.remainingInput!.destroy();
        this.remainingInput = undefined;
        this.$element.remove();
    }

    public appendTo($element: JQuery): StockTableEditor {
        this.$element.appendTo($element);

        return this;
    }
}
